import React from 'react';
import { Card, CardContent, Typography, Box, useTheme, useMediaQuery } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { useTranslation } from 'react-i18next';
import { ServiceStatisticsItem } from '../../types/statistics';

interface ServicesDonutChartProps {
  servicesData: ServiceStatisticsItem[];
}

const ServicesDonutChart: React.FC<ServicesDonutChartProps> = ({ servicesData }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const totalCount = servicesData.reduce((sum, item) => sum + item.count, 0);

  // Helper function to truncate text with ellipsis
  const truncateText = (text: string, maxLength: number = 30): string => {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + '...';
  };

  const chartData = servicesData.map((item, index) => {
    const percentage = totalCount > 0 ? (item.count / totalCount) * 100 : 0;
    const roundedPercentage = Math.round(percentage * 10) / 10;
    const truncatedServiceName = truncateText(item.serviceName, 25);
    return {
      id: index,
      value: item.count,
      label: `${truncatedServiceName}: ${item.count} (${roundedPercentage}%)`,
      serviceName: item.serviceName,
      count: item.count,
      percentage: roundedPercentage,
    };
  });

  if (chartData.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('statistics.servicesStatistics')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <Typography variant="body1" color="text.secondary">
              {t('statistics.noData')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const chartHeight = isMobile ? 300 : 400;
  const innerRadius = isMobile ? 40 : 60;
  const outerRadius = isMobile ? 80 : 120;
  const legendMaxHeight = isMobile ? 250 : 400;
  const legendSpacing = isMobile ? '0px' : '40px'; // Equal spacing on both sides
  const chartMargin = isMobile 
    ? { top: 20, right: 10, bottom: 20, left: 10 }
    : { top: 20, right: 20, bottom: 20, left: 20 };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('statistics.servicesStatistics')}
        </Typography>
        <Box 
          sx={{ 
            width: '100%', 
            height: isMobile ? 'auto' : chartHeight, 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'center',
            alignItems: isMobile ? 'center' : 'flex-start',
            gap: isMobile ? 2 : 0,
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              '& .MuiChartsLegend-root': {
                marginLeft: `${legendSpacing} !important`,
                marginTop: isMobile ? '20px !important' : '0px !important',
                paddingRight: `${legendSpacing} !important`,
                maxHeight: `${legendMaxHeight}px`,
                overflowY: 'auto',
                overflowX: 'hidden',
                display: 'flex !important',
                flexDirection: 'column !important',
                flexWrap: 'nowrap !important',
                width: isMobile ? '100%' : 'auto',
                '& > *': {
                  width: '100% !important',
                  flexBasis: 'auto !important',
                },
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '10px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '10px',
                  '&:hover': {
                    background: '#555',
                  },
                },
              },
              '& .MuiChartsLegend-root > *': {
                display: 'flex !important',
                flexDirection: 'column !important',
                flexWrap: 'nowrap !important',
              },
            }}
          >
            <PieChart
              series={[
                {
                  data: chartData,
                  innerRadius,
                  outerRadius,
                  paddingAngle: 2,
                  cornerRadius: 5,
                  arcLabel: () => '', // No labels on the donut, use legend and tooltip instead
                  arcLabelMinAngle: 10,
                  valueFormatter: () => ''
                }
              ]}
              height={chartHeight}
              margin={chartMargin}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ServicesDonutChart;

